<script setup>
/**
 * FormControlThemesCssGroups
 * A UI component for managing a list of CSS group names.
 * It allows users to add groups from a predefined list or create new ones.
 * Added groups are displayed as removable tags.
 */
import { ref, computed, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
});

const groupStr = ref('');

const groups = computed(() => props.currentData.groups);

const groupNames = computed(() => {
    const allGroupNames = store.state.themes.cssGroupNames || [];
    if (!groups.value) {
        return allGroupNames;
    }
    // Filter out already selected groups
    return allGroupNames.filter(x => !groups.value.includes(x));
});

// Set default value on setup, equivalent to 'created' hook
store.dispatch(`${props.formId}/setDefaultValue`, {
    name: 'groups',
    value: [],
});

const addGroup = (group) => {
    group = group.trim();
    if (group !== '') {
        // Ensure the 'groups' array exists before pushing
        if (!Array.isArray(groups.value)) {
            props.currentData.groups = [];
        }
        if (!groups.value.includes(group)) {
            groups.value.push(group);
        }
        groupStr.value = '';
    }
};

const createGroup = () => {
    const str = groupStr.value;
    if (/,/.test(str)) {
        const newGroups = str.split(',');
        newGroups.forEach(group => {
            addGroup(group);
        });
    } else {
        addGroup(str);
    }
};

const removeGroup = (index) => {
    if (Array.isArray(groups.value)) {
        groups.value.splice(index, 1);
    }
};
</script>

<template>
    <div>
        <form class="mb-3 d-flex align-items-center" @submit.prevent="createGroup">
            <div class="dropdown">
                <input v-model="groupStr" class="form-control form-control-lg" data-bs-toggle="dropdown" @keydown.enter.prevent="createGroup">

                <div v-show="groupNames.length" class="dropdown-menu">
                    <a v-for="group in groupNames" :key="group" class="dropdown-item" @click="addGroup(group)">
                        {{ group }}
                    </a>
                </div>
            </div>

            <button class="ms-1 btn btn-sm btn-light" type="submit">
                <i class="bi-plus-lg"/>
            </button>
        </form>

        <div class="d-flex align-items-center flex-wrap">
            <span v-for="(group, index) in groups" :key="group" class="me-2 mb-2 bg-dark text-light border rounded p-1 ps-2 d-flex align-items-center">
                {{ group }}
                <button aria-label="Close" class="btn-close btn-close-white ms-1" type="button" @click="removeGroup(index)"></button>
            </span>
        </div>
    </div>
</template>
